/**
 * TCX-7 — Training Domain-Match Policy — shop-manager settings section.
 *
 * Mountable in the Shop Profile / settings area. Shop-scoped only: a
 * manager sees their shop's effective policy (and where it's inherited
 * from), sets a shop mode override, manages shop-scoped approved-domain
 * rules, reviews their shop's mismatch diagnostics, generates + reviews
 * candidates, and reads their shop's audit history.
 *
 * The RPCs enforce authority server-side (actor === auth.uid() and
 * shop-manager scope). This component renders for shop-profile editors;
 * unauthorized calls surface the friendly permission error.
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Globe } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  AuditSection,
  CandidatesManager,
  DiagnosticsSection,
  EffectivePolicySection,
  PolicyDisclaimer,
  RulesManager,
} from '@/components/training/trainingDomainPolicyCommon';

export function TrainingDomainPolicyShopSettings() {
  const { activeShop, user } = useAuth();
  const shopId = activeShop?.id ?? null;
  const actor = user?.id ?? null;

  if (!shopId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Select a shop to configure its training domain policy.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="tdp-shop-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-5 w-5" />
            Training Approved Domains
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <PolicyDisclaimer />

          <EffectivePolicySection
            title="Your shop's effective policy"
            description="Resolved for this shop — inherits the provider/global default unless you set a shop override below."
            resolveShopId={shopId}
            resolveProviderId={null}
            resolveArtifactId={null}
            writeCtx={{ scope: 'shop', providerId: null, shopId }}
            actor={actor}
            canWrite
          />

          <RulesManager
            ctx={{ scope: 'shop', providerId: null, shopId }}
            actor={actor}
            canWrite
            title="Your shop's approved domains"
          />

          <CandidatesManager shopId={shopId} actor={actor} canWrite showGenerate />

          <DiagnosticsSection
            shopId={shopId}
            description="Host mismatches for your shop. Review these before enabling strict or allowlist mode."
          />

          <AuditSection shopId={shopId} />
        </CardContent>
      </Card>
    </div>
  );
}
